<template>
  <esg-base-chart height="550px">
    <loading :active="loading"
             :z-index="1000"
             :is-full-page="false"/>
    <apex-chart ref="chart"
                type="bar"
                v-show="!loading"
                :width=this.width
                :height=this.height
                :options="chartOptions"
                :series="series"/>
  </esg-base-chart>
</template>

<script>
import EsgBaseChart from "@/components/esg/base/EsgBaseChart.vue";

export default {
  components: { EsgBaseChart },
  props: {
    width: {
      type: String,
      default: '100%'
    },
    height: {
      type: String,
      default: '550'
    },
    corporation: {
      type: Object,
      required: true
    },
    sort: {
      type: String,
      default: 'asc'
    }
  },
  data() {
    return {
      loading: false,
      fuels: [],
      ascCategories: [],
      descCategories: [],
      ascSeries: [],
      descSeries: [],
      series: [],
      chartOptions: {
        chart: {
          toolbar: {
            show: false
          }
        },
        plotOptions: {
          bar: {
            horizontal: true,
            dataLabels: {
              position: '',
              hideOverflowingLabels: false,
            }
          }
        },
        dataLabels: {
          enabled: true,
          enabledOnSeries: [0],
          textAnchor: 'center',
          formatter: (val) => this.formatNumber(val, 2),
          style: {
            colors: ['#000'],
            fontWeight: 'normal',
          }
        },
        stroke: {
          show: true,
          width: 1,
          colors: ['#fff']
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: (val, opt) => {
              const dataPointIndex = opt.dataPointIndex;
              const seriesIndex = opt.seriesIndex;
              const dataPoint = opt.series[seriesIndex][dataPointIndex];
              const total = opt.series[seriesIndex].reduce((a, b) => a + b, 0);
              const share = 100 * dataPoint / total;
              return `${this.formatNumber(dataPoint, 2)} TJ (${this.formatNumber(share, 2)}%)`
            }
          }
        },
        xaxis: {
          categories: [],
          labels: {
            show: true,
          },
          title: {
            text: '에너지 사용량 (TJ)',
            style: {
              fontSize: '12px',
              fontWeight: 'normal'
            }
          }
        },
        legend: {
          position: 'right',
          onItemClick: {
            toggleDataSeries: false
          },
        },
        grid: {
          show: false,
        },
        responsive: [
          {
            breakpoint: 550,
            options: {
              legend: {
                position: 'bottom'
              }
            }
          }
        ]
      }
    }
  },
  watch: {
    corporation: {
      deep: true,
      immediate: true,
      handler() {
        this.fetchData();
      }
    },
    sort: {
      immediate: true,
      handler(sort) {
        this.sortSeries(sort);
      }
    }
  },
  beforeDestroy() {
    if (this.abortController) {
      this.abortController.abort();
    }
  },
  methods: {
    async fetchData() {
      if (!this.corporation || !this.corporation.code)
        return;

      try {
        this.loading = true;

        let year = new Date().getFullYear();
        let years = [year, year - 1, year - 2];
        let args = [{
          years,
          scopes: [1, 2],
          corporations: [this.corporation.code],
          groupKeys: ['year', 'fuel_code', 'fuel_name']
        }];

        let data = new FormData()
        data.append('path', 'esg/energy_usage/main');
        data.append('json', JSON.stringify(args));

        this.abortController = new AbortController();
        let response = await axios.post('esg/lca/run/python/chart-data', data, {
          headers: { Authorization: accessToken() },
          signal: this.abortController.signal
        });

        if (!response.data.data) {
          this.series = [];
          return;
        }

        let list = JSON.parse(response.data.data)[0].data;

        // Extract unique fuel names
        this.fuels = Array.from(new Set(list.map(row => row.fuel_name)));

        // Totals for the last three years
        let totals = this.fuels.map(fuel =>
            list.filter(row => row.fuel_name === fuel)
                .map(row => row.values)
                .reduce((a, b) => a + b, 0)
        );

        // Sort fuels based on the totals in descending order
        this.descCategories = this.fuels.slice();
        this.descCategories.sort((a, b) => {
          let indA = this.fuels.findIndex(fuel => fuel === a);
          let indB = this.fuels.findIndex(fuel => fuel === b);
          return totals[indB] - totals[indA];
        });

        // Reverse the categories sorted in descending order
        this.ascCategories = this.descCategories.slice();
        this.ascCategories.reverse();

        // Construct series
        this.descSeries = [];
        this.ascSeries = [];
        years.forEach(year => {
          let descData = this.descCategories.map(fuel => {
            let row = list.find(row => row.year === year && row.fuel_name === fuel)
            return row ? row.values : 0;
          });
          let ascData = descData.slice();
          ascData.reverse();

          this.descSeries.push({
            name: year,
            data: descData.slice(0, 10)
          });
          this.ascSeries.push({
            name: year,
            data: ascData.slice(0, 10)
          });
        });

        // Slice categories
        this.ascCategories = this.ascCategories.slice(0, 10);
        this.descCategories = this.descCategories.slice(0, 10);

        // Select data set according to the sorting order
        this.sortSeries(this.sort);
      } catch (error) {
        this.loading = false;
        await this.showError(error);
      } finally {
        this.loading = false;
        this.refreshChart();
      }
    },
    sortSeries(sort) {
      if (sort === 'desc') {
        this.series = this.descSeries
        this.chartOptions.xaxis.categories = this.descCategories;
      } else {
        this.series = this.ascSeries;
        this.chartOptions.xaxis.categories = this.ascCategories;
      }
      this.refreshChart();
    }
  }
}
</script>
<style>
</style>